import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';

const TOPBAR_CACHE_KEY = 'ubunifu-topbar-profile-cache-v1';

let memoryCache = null;

function readStoredCache() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawCache = window.sessionStorage.getItem(TOPBAR_CACHE_KEY);
    return rawCache ? JSON.parse(rawCache) : {};
  } catch (error) {
    return {};
  }
}

function getCacheStore() {
  if (!memoryCache) {
    memoryCache = readStoredCache();
  }

  return memoryCache;
}

function persistCacheStore(nextCache) {
  memoryCache = nextCache;

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(TOPBAR_CACHE_KEY, JSON.stringify(nextCache));
  } catch (error) {
    // Ignore cache write failures and continue with live data.
  }
}

function getCacheEntry(cacheKey) {
  return getCacheStore()[cacheKey] || null;
}

function setCacheEntry(cacheKey, value) {
  const currentCache = getCacheStore();
  persistCacheStore({
    ...currentCache,
    [cacheKey]: value,
  });
}

function clearCacheEntry(cacheKey) {
  const currentCache = { ...getCacheStore() };
  delete currentCache[cacheKey];
  persistCacheStore(currentCache);
}

function normaliseProfile(data, fallbackImage) {
  return {
    profilePic: data?.profilePic || fallbackImage,
    displayName: data?.bio || data?.userName || '',
    admissionNumber: data?.admissionNumber ? String(data.admissionNumber) : '',
    updatedAt: Date.now(),
  };
}

export default function useTopbarProfile({
  profileCollection,
  profileIdField,
  expectedUserType,
  redirectPath,
  fallbackImage,
}) {
  const auth = getAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    profilePic: fallbackImage,
    displayName: '',
    admissionNumber: '',
    userType: '',
    ready: false,
  });

  useEffect(() => {
    let unsubscribeProfile = () => {};
    let unsubscribeRole = () => {};

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      unsubscribeProfile();
      unsubscribeRole();

      if (!user) {
        setProfile({
          profilePic: fallbackImage,
          displayName: '',
          admissionNumber: '',
          userType: '',
          ready: true,
        });
        return;
      }

      const cacheKey = `${profileCollection}:${user.uid}`;
      const cachedProfile = getCacheEntry(cacheKey);

      if (cachedProfile) {
        setProfile({
          ...cachedProfile,
          ready: true,
        });
      } else {
        setProfile((currentProfile) => ({
          ...currentProfile,
          profilePic: fallbackImage,
          ready: false,
        }));
      }

      const profileQuery = query(
        collection(db, profileCollection),
        where(profileIdField, '==', user.uid)
      );

      unsubscribeProfile = onSnapshot(
        profileQuery,
        (snapshot) => {
          if (snapshot.empty) {
            clearCacheEntry(cacheKey);
            setProfile((currentProfile) => ({
              ...currentProfile,
              profilePic: fallbackImage,
              displayName: '',
              admissionNumber: '',
              ready: true,
            }));
            return;
          }

          const nextProfile = normaliseProfile(snapshot.docs[0].data(), fallbackImage);
          const cachedWithRole = {
            ...(getCacheEntry(cacheKey) || {}),
            ...nextProfile,
          };

          setCacheEntry(cacheKey, cachedWithRole);
          setProfile((currentProfile) => ({
            ...currentProfile,
            ...cachedWithRole,
            ready: true,
          }));
        },
        () => {
          setProfile((currentProfile) => ({
            ...currentProfile,
            ready: true,
          }));
        }
      );

      const roleQuery = query(collection(db, 'users'), where('userId', '==', user.uid));

      unsubscribeRole = onSnapshot(roleQuery, (snapshot) => {
        if (snapshot.empty) {
          return;
        }

        const userType = snapshot.docs[0].data().userType || '';
        const mergedProfile = {
          ...(getCacheEntry(cacheKey) || normaliseProfile(null, fallbackImage)),
          userType,
        };

        setCacheEntry(cacheKey, mergedProfile);
        setProfile((currentProfile) => ({
          ...currentProfile,
          ...mergedProfile,
          ready: true,
        }));

        if (expectedUserType && userType !== expectedUserType && redirectPath) {
          navigate(redirectPath);
        }
      });
    });

    return () => {
      unsubscribeProfile();
      unsubscribeRole();
      unsubscribeAuth();
    };
  }, [auth, expectedUserType, fallbackImage, navigate, profileCollection, profileIdField, redirectPath]);

  return profile;
}
